import { useEffect, useState } from 'react';
import { formatCountdown } from '@pickem/shared';
import { serverClock } from '../api/client';

/**
 * A ticking clock anchored to the server.
 *
 * Reads `serverClock`, not `Date.now()`, so a device whose clock is wrong still counts
 * down to the right moment. This drives display only — whether a pick is accepted is
 * decided by Postgres, and a countdown that hits zero simply triggers a refetch rather
 * than the client deciding the game is locked on its own.
 */
export function useServerTick(intervalMs = 1000): Date {
  const [now, setNow] = useState(() => serverClock.now());

  useEffect(() => {
    const handle = setInterval(() => setNow(serverClock.now()), intervalMs);
    return () => clearInterval(handle);
  }, [intervalMs]);

  return now;
}

/** `2d 4h`, `3h 12m`, `14m`, or `Locked`. */
export function useCountdown(kickoffAtIso: string | null | undefined): string | null {
  const now = useServerTick();
  if (!kickoffAtIso) return null;
  return formatCountdown(Date.parse(kickoffAtIso) - now.getTime());
}

/** A countdown formatter for a list, so a screen renders one tick for many games. */
export function useCountdownFactory(): (kickoffAtIso: string) => string {
  const now = useServerTick();
  return (kickoffAtIso: string) => formatCountdown(Date.parse(kickoffAtIso) - now.getTime());
}
